import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Text,
  Stack,
  Button,
  Textarea,
  Spinner,
  useToast,
  Avatar,
  Flex,
  useColorModeValue,
  Divider,
  HStack,
} from "@chakra-ui/react";
import { useParams, useHistory } from "react-router-dom";
import { FiTrash2 } from "react-icons/fi";
import axios from "axios";
import dayjs from "dayjs"; 
import { getID, getRole } from "utils/auth";  // Assuming getRole() is a function to get user role
import { getToken } from "utils/auth";
import config from "config";

const PostDetails = () => {
  const { id } = useParams();
  const history = useHistory();
  const toast = useToast();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [postNotFound, setPostNotFound] = useState(false); // New state to track if post is found

  const bgColor = useColorModeValue("gray.50", "gray.800");
  const boxBgColor = useColorModeValue("white", "gray.700");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const secondaryTextColor = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.300", "gray.600");
  const headingColor = useColorModeValue("teal.600", "teal.300");

  const fetchPostDetails = async () => {
    try {
      const token = getToken()
      const response = await axios.get(`${config.API_URL}/forums/posts/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        });
      
      if (response.status === 404) {
        setPostNotFound(true);
        setLoading(false);
        return;
      }
      console.log(response.data.comments)
      setPost(response.data);
      console.log(response.data)
      console.log(Object.keys(response.data),"ww")
      setComments(response.data.comments);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load post details.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setPostNotFound(true); // If error occurs, set postNotFound to true
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast({
        title: "Empty Comment",
        description: "Please enter a comment before submitting.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${config.API_URL}/forums/comments`,
        { post_id: parseInt(id), content: newComment },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 201) {
        setNewComment("");
        fetchPostDetails();
        toast({
          title: "Comment Added",
          description: "Your comment has been added successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong while adding the comment.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const token = getToken();
      const response = await axios.delete(`${config.API_URL}/forums/comments/${commentId}`,{headers : { Authorization: `Bearer ${token}` }});
      if (response.status === 200) {
        toast({
          title: "Comment Deleted",
          description: "The comment has been deleted successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        fetchPostDetails();  // Re-fetch the post details after deleting a comment
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete the comment.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeletePost = async () => {
    try {
      const token = getToken()
      const response = await axios.delete(`${config.API_URL}/forums/posts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 204) {
        toast({
          title: "Post Deleted",
          description: "The post has been deleted successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        history.push("/txd/forums");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete the post.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchPostDetails();
  }, [id]);

  return (
    <Box p={6} bg={bgColor} borderRadius="lg" boxShadow="xl" maxW="container.md" mx="auto">
      {loading ? (
        <Flex justify="center" align="center" minH="60vh">
          <Spinner size="xl" color="teal.500" />
        </Flex>
      ) : postNotFound ? ( // Check if post is not found
        <Box p={6} bg={boxBgColor} borderRadius="lg" boxShadow="md" textAlign="center">
          <Heading size="lg" color={headingColor}>Post Not Found</Heading>
          <Text fontSize="lg" color={secondaryTextColor} mt={4}>
            Sorry, the post you are looking for could not be found.
          </Text>
        </Box>
      ) : (
        <>
          <Box p={6} bg={boxBgColor} borderRadius="lg" boxShadow="md" mb={6}>
            <Flex align="center" mb={4}>
              <Avatar
                name={`${post.user.first_name} ${post.user.last_name}`}
                src={post.user.avatarUrl}
                size="lg"
                mr={4}
              />
              <Box>
                <Heading size="lg" color={headingColor} mb={1}
                style={{
                  whiteSpace: 'normal',  // Allows wrapping
                  wordBreak: 'break-word'  // Breaks long words to prevent overflow
                }}
                >
                  {post.title}
                </Heading>
                <Text fontSize="sm" color={secondaryTextColor}>
                  Posted by {post.user.first_name} {post.user.last_name} on{" "}
                  {(new Date(post['created_at'])).toLocaleString() }
                </Text>
              </Box>
            </Flex>
            <Text fontSize="lg" color={textColor} mb={4}>
              {post.content}
            </Text>
            {(getID() === post.user?.ID || getRole() === "admin" || (getRole() === "moderator" && post.user?.role !== "admin")) && (
              <Button
                colorScheme="red"
                leftIcon={<FiTrash2 />}
                onClick={handleDeletePost}
                _hover={{ bg: "red.600" }}
              >
                Delete Post
              </Button>
            )}
          </Box>

          <Stack spacing={6}>
            <Heading size="md" color={headingColor}>
              Comments
            </Heading>
            <Divider />
            {comments.length > 0 ? (
              comments.map((comment) => (
                <Box
                  key={comment.ID}
                  p={4}
                  bg={boxBgColor}
                  borderRadius="lg"
                  boxShadow="sm"
                  borderWidth="1px"
                  borderColor={borderColor}
                  _hover={{ shadow: "md" }}
                >
                  <Flex align="center" mb={3}>
                    <Avatar
                      name={`${comment.user.first_name} ${comment.user.last_name}`}
                      src={comment.user.avatarUrl}
                      size="sm"
                      mr={3}
                    />
                    <Box>
                      <Text fontWeight="bold" color={textColor}>
                        {comment.user.first_name} {comment.user.last_name}
                      </Text>
                      <Text fontSize="sm" color={secondaryTextColor}>
                        Commented on {(new Date(comment['created_at'])).toLocaleString() }
                      </Text>
                    </Box>
                  </Flex>
                  <Text color={textColor}>{comment.content}</Text>
                  {(getID() === comment.user?.ID || getRole() === "admin" || (getRole() === "moderator" && comment.user?.role !== "admin")) && (
                    <Button
                      colorScheme="red"
                      leftIcon={<FiTrash2 />}
                      onClick={() => handleDeleteComment(comment.ID)}
                      size="sm"
                      mt={2}
                      _hover={{ bg: "red.600" }}
                    >
                      Delete Comment
                    </Button>
                  )}
                </Box>
              ))
            ) : (
              <Text color={secondaryTextColor} fontStyle="italic">
                No comments yet. Be the first to comment!
              </Text>
            )}

            <Textarea
              placeholder="Write a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              resize="vertical"
              borderColor={borderColor}
              _focus={{ borderColor: "teal.500" }}
              mb={4}
              bg={boxBgColor}
              color={textColor}
            />
            <Button
              colorScheme="blue"
              onClick={handleAddComment}
              isDisabled={!newComment.trim()}
              _hover={{ bg: "blue.600" }}
            >
              Add Comment
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
};

export default PostDetails;
